export const DEFAULT_HINT_COSTS = [5, 5, 5];
export const DEFAULT_WIN_FAN_ENERGY = 50;
export const PAGE_SIZE = 20;
export const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const SOURCE_META = {
  ai: { label: 'AI', color: 'info' },
  admin: { label: 'Admin', color: 'success' },
  fallback: { label: 'Fallback', color: 'warning' },
};

export const STATUS_META = {
  draft: { label: 'Draft', color: 'warning' },
  published: { label: 'Published', color: 'success' },
};

export const CATEGORIES = [
  { value: 'player', label: 'Player' },
  { value: 'shot', label: 'Shot' },
  { value: 'fielding', label: 'Fielding' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'game_term', label: 'Game term' },
];

export const DIFFICULTIES = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

export function categoryLabel(value) {
  return CATEGORIES.find((option) => option.value === value)?.label || 'Not set';
}

export function difficultyLabel(value) {
  return DIFFICULTIES.find((option) => option.value === value)?.label || 'Not set';
}

export function wordLength(day) {
  const stored = Number(day?.word_length);
  if (stored === 4 || stored === 5 || stored === 6) return stored;
  const letters = String(day?.answer || '').trim().length;
  return letters >= 4 && letters <= 6 ? letters : null;
}

export function addDays(dayKey, days) {
  if (!dayKey || !DAY_KEY_PATTERN.test(dayKey)) return '';
  const [year, month, day] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** YYYY-MM-DD in Asia/Kolkata, shifted by whole days. */
export function istDayKey(offsetDays = 0) {
  const key = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return addDays(key, offsetDays);
}

export function formatIstDateTime(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function sourceMeta(source) {
  return SOURCE_META[source] || { label: source || 'Unknown', color: 'default' };
}

export function statusMeta(status) {
  return STATUS_META[status] || { label: status || 'Unknown', color: 'default' };
}

export function playerCount(day, key) {
  const value = day?.players?.[key];
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

export function hintCosts(day) {
  const costs = day?.hint_fan_energy_costs;
  if (Array.isArray(costs) && costs.length === 3) return costs.map((value) => Number(value));
  return DEFAULT_HINT_COSTS;
}

/** Max Fan Energy for a win with no extra hints revealed. */
export function winFanEnergy(day) {
  const value = Number(day?.win_fan_energy);
  if (Number.isInteger(value) && value >= 0) return value;
  return DEFAULT_WIN_FAN_ENERGY;
}

/** Win reward after every extra hint is revealed. Never below 0. */
export function rewardAfterAllHints(day) {
  const deducted = hintCosts(day).reduce((sum, cost) => sum + (Number(cost) || 0), 0);
  return Math.max(0, winFanEnergy(day) - deducted);
}

/** Hint fields are plain strings. An object is read as its text, never rendered as an object. */
export function hintText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const text = value.text ?? value.hint ?? value.content ?? value.value ?? value.label;
    if (typeof text === 'string') return text;
    if (text != null && typeof text !== 'object') return String(text);
  }
  return '';
}

export function extraHintTexts(values) {
  const list = Array.isArray(values) ? values : [];
  return [0, 1, 2].map((index) => hintText(list[index]));
}

export function normalizePuzzle(raw, dayKey) {
  const day = withDayKey(raw, dayKey);
  if (!day) return null;
  return {
    ...day,
    title: day.title == null || day.title === '' ? null : hintText(day.title),
    main_hint: hintText(day.main_hint),
    extra_hints: extraHintTexts(day.extra_hints),
  };
}

export function emptyForm() {
  return {
    answer: '',
    category: '',
    difficulty: '',
    title: '',
    main_hint: '',
    extra_hints: ['', '', ''],
  };
}

export function dayToForm(day) {
  const category = CATEGORIES.some((option) => option.value === day?.category) ? day.category : '';
  const difficulty = DIFFICULTIES.some((option) => option.value === day?.difficulty) ? day.difficulty : '';
  return {
    answer: day?.answer || '',
    category,
    difficulty,
    title: day?.title || '',
    main_hint: hintText(day?.main_hint),
    extra_hints: extraHintTexts(day?.extra_hints),
  };
}

function containsAnswer(text, answer) {
  return String(text || '').toUpperCase().includes(answer);
}

export function validatePitchWordForm(form) {
  const answer = String(form?.answer || '').trim().toUpperCase();
  if (!/^[A-Z]{4,6}$/.test(answer)) {
    return 'Answer must be 4 to 6 letters, A–Z.';
  }

  const fields = [
    ['Title', form?.title],
    ['Main hint', form?.main_hint],
  ];
  for (const [label, value] of fields) {
    const text = String(value || '').trim();
    if (!text) return `${label} is required.`;
    if (containsAnswer(text, answer)) return `${label} contains the answer.`;
  }

  const extras = Array.isArray(form?.extra_hints) ? form.extra_hints : [];
  if (extras.length !== 3) return 'Exactly 3 extra hints are required.';
  for (let index = 0; index < 3; index += 1) {
    const hint = String(extras[index] || '').trim();
    if (!hint) return `Extra hint ${index + 1} is required.`;
    if (containsAnswer(hint, answer)) return `Extra hint ${index + 1} contains the answer.`;
  }

  return '';
}

function preservedCosts(day) {
  const costs = day?.hint_fan_energy_costs;
  if (!Array.isArray(costs) || costs.length !== 3) return null;
  const nums = costs.map((value) => Number(value));
  if (nums.some((value) => !Number.isInteger(value) || value < 0)) return null;
  return nums;
}

export function formToPayload(form, day, { status, force = false } = {}) {
  const payload = {
    status: status === 'published' ? 'published' : 'draft',
    answer: String(form.answer || '').trim().toUpperCase(),
    title: String(form.title || '').trim(),
    main_hint: String(form.main_hint || '').trim(),
    extra_hints: (form.extra_hints || []).map((hint) => String(hint || '').trim()),
    force: !!force,
  };

  if (CATEGORIES.some((option) => option.value === form.category)) {
    payload.category = form.category;
  }
  if (DIFFICULTIES.some((option) => option.value === form.difficulty)) {
    payload.difficulty = form.difficulty;
  }

  const costs = preservedCosts(day);
  if (costs) payload.hint_fan_energy_costs = costs;

  if (day?.win_fan_energy != null && Number.isInteger(Number(day.win_fan_energy))) {
    payload.win_fan_energy = Number(day.win_fan_energy);
  }

  return payload;
}

export function withDayKey(payload, dayKey) {
  if (!payload || typeof payload !== 'object') return null;
  const day = payload.day_key || payload.answer || payload.status
    ? payload
    : payload.puzzle || payload.day;
  if (!day || typeof day !== 'object') return null;
  if (!day.answer && !day.day_key && !day.status && !day.title) return null;
  return {
    ...day,
    day_key: day.day_key || day.date || dayKey,
  };
}

export function extractWriteStats(payload) {
  const source = payload && typeof payload === 'object' ? payload : {};
  const nested = source.day && typeof source.day === 'object' ? source.day : {};
  const read = (key) => (source[key] != null ? source[key] : nested[key]);
  return {
    in_progress_updated: read('in_progress_updated'),
    in_progress_finalized: read('in_progress_finalized'),
    finished_left_unchanged: read('finished_left_unchanged'),
  };
}

export function formatWriteStats(stats) {
  const updated = Number(stats?.in_progress_updated);
  const finalized = Number(stats?.in_progress_finalized);
  const unchanged = Number(stats?.finished_left_unchanged);
  const parts = [];
  if (updated > 0) parts.push(`${updated} in progress updated`);
  if (finalized > 0) parts.push(`${finalized} finalized`);
  if (unchanged > 0) parts.push(`${unchanged} finished left on the old word`);
  return parts.join(' · ');
}

export function mergeDay(previous, next) {
  if (!next) return previous;
  return {
    ...(previous || {}),
    ...next,
    players: next.players || previous?.players,
    source: next.source || previous?.source,
    day_key: next.day_key || previous?.day_key,
  };
}
